import React, { useState, useEffect } from "react";
import {
    Box, Card, CardContent, Typography, IconButton, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Snackbar, Alert, Tooltip,
} from "@mui/material";
import PersonRoundedIcon       from "@mui/icons-material/PersonRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import InventoryRoundedIcon    from "@mui/icons-material/Inventory2Rounded";
import MapRoundedIcon          from "@mui/icons-material/MapRounded";
import VisibilityRoundedIcon   from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon         from "@mui/icons-material/EditRounded";
import SaveRoundedIcon         from "@mui/icons-material/SaveRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SettingsRoundedIcon     from "@mui/icons-material/SettingsRounded";
import { useNavigate } from "react-router-dom";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B",
};

// ── Navigable settings card ───────────────────────────────────────────────
const NavCard = ({ title, subtitle, icon, iconBg, iconColor, accent, onClick, children }) => (
    <Card
        onClick={onClick}
        sx={{
            borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)",
            cursor:onClick?"pointer":"default", overflow:"hidden", height:"100%",
            transition:"transform 0.18s, box-shadow 0.18s",
            "&:hover":onClick?{ transform:"translateY(-2px)", boxShadow:`0 6px 20px rgba(0,124,132,0.10)` }:{},
        }}
    >
        <Box sx={{ height:3, background:`linear-gradient(90deg,${accent},${accent}66)` }} />
        <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
            <Box sx={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", mb:1.5 }}>
                <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
                    <Box sx={{ width:38,height:38,borderRadius:"10px",bgcolor:iconBg,display:"flex",alignItems:"center",justifyContent:"center" }}>
                        {React.cloneElement(icon, { sx:{ fontSize:20,color:iconColor } })}
                    </Box>
                    <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{title}</Typography>
                </Box>
                {onClick && <ArrowForwardRoundedIcon sx={{ fontSize:18,color:T.muted,mt:0.3 }} />}
            </Box>
            {subtitle && <Typography sx={{ fontSize:12,color:T.muted,mb:children?1.5:0,lineHeight:1.5 }}>{subtitle}</Typography>}
            {children}
        </CardContent>
    </Card>
);

// ── Google Map Key Card ───────────────────────────────────────────────────
function GoogleMapKeyCard() {
    const [apiKey,        setApiKey]        = useState("");
    const [displayKey,    setDisplayKey]    = useState("********************");
    const [editMode,      setEditMode]      = useState(false);
    const [otpOpen,       setOtpOpen]       = useState(false);
    const [otp,           setOtp]           = useState("");
    const [mobileNum,     setMobileNum]     = useState("");
    const [actionType,    setActionType]    = useState("");
    const [snack,         setSnack]         = useState({ open:false, msg:"", sev:"info" });

    useEffect(() => {
        const u = JSON.parse(localStorage.getItem("userData") || "{}");
        if (u?.mobile_num) setMobileNum(u.mobile_num);
    }, []);

    const toast = (msg, sev="info") => setSnack({ open:true, msg, sev });

    const fetchKey = async () => {
        try {
            const res = await getData(API_ENDPOINTS.getAllCharges);
            const keyObj = (res?.message?.data||[]).find(c=>c.key==="google_api_key");
            if (keyObj?.value_text) { setApiKey(keyObj.value_text); setDisplayKey(keyObj.value_text); }
        } catch(_) { toast("Failed to fetch API key","error"); }
    };

    const handleOtpRequest = async (type) => {
        try {
            const res = await postData(API_ENDPOINTS.sendOtp, { mobile_num:mobileNum });
            if (res.status==="SUCCESS") {
                setOtpOpen(true); setActionType(type);
                toast(`OTP sent! OTP: ${res.result.otp}`,"success");
            } else toast(res.message||"Failed to send OTP","error");
        } catch(_) { toast("Error sending OTP","error"); }
    };

    const handleOtpSubmit = async () => {
        try {
            const res = await postData(API_ENDPOINTS.verifyOtpLogin, { mobile_num:mobileNum, otp });
            if (res.status==="SUCCESS") {
                setOtpOpen(false); setOtp("");
                await fetchKey();
                if (actionType==="edit") setEditMode(true);
                toast("OTP verified!","success");
            } else toast(res.message||"OTP failed","error");
        } catch(_) { toast("Error verifying OTP","error"); }
    };

    const handleSave = async () => {
        try {
            const resAll = await getData(API_ENDPOINTS.getAllCharges);
            const charges = resAll?.statusCode===200 ? resAll.message.data||[] : [];
            const existing = charges.find(c=>c.key==="google_api_key");
            const res = existing
                ? await postData(API_ENDPOINTS.updateCharge, { key:"google_api_key",value_text:apiKey })
                : await postData(API_ENDPOINTS.generateCharges, { key:"google_api_key",value_text:apiKey,value_numeric:0 });
            if (res.statusCode===200) { toast("API key saved!","success"); setEditMode(false); setDisplayKey("********************"); }
            else toast("Failed to save","error");
        } catch(_) { toast("Something went wrong","error"); }
    };

    const maskMobile = n => n ? n.slice(0,4)+"X".repeat(n.length-4) : "";

    return (
        <>
            <NavCard
                title="Google Maps API Key"
                subtitle="Manage your Google Maps API key. OTP verification required to view or edit."
                icon={<MapRoundedIcon />}
                iconBg={T.roseLt}
                iconColor="#E11D48"
                accent="#E11D48"
            >
                <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                    <TextField
                        value={displayKey}
                        onChange={e=>{ setDisplayKey(e.target.value); setApiKey(e.target.value); }}
                        size="small" fullWidth disabled={!editMode}
                        InputProps={{ sx:{ borderRadius:"10px", fontFamily:"monospace", fontSize:13 } }}
                        sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }}
                    />
                    {!editMode ? (
                        <>
                            <Tooltip title="View key (OTP required)">
                                <IconButton size="small" onClick={()=>handleOtpRequest("view")} sx={{ color:T.teal,bgcolor:T.tealLt,borderRadius:"8px" }}>
                                    <VisibilityRoundedIcon sx={{ fontSize:17 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit key (OTP required)">
                                <IconButton size="small" onClick={()=>handleOtpRequest("edit")} sx={{ color:T.amber,bgcolor:T.amberLt,borderRadius:"8px" }}>
                                    <EditRoundedIcon sx={{ fontSize:17 }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <Tooltip title="Save">
                            <IconButton size="small" onClick={handleSave} sx={{ color:T.emerald,bgcolor:T.emeraldLt,borderRadius:"8px" }}>
                                <SaveRoundedIcon sx={{ fontSize:17 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </NavCard>

            {/* OTP Dialog */}
            <Dialog open={otpOpen} onClose={()=>setOtpOpen(false)} PaperProps={{ sx:{ borderRadius:"16px" } }}>
                <DialogTitle sx={{ fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>OTP Verification</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize:13,color:T.muted,mb:2 }}>
                        Enter the OTP sent to {maskMobile(mobileNum)}
                    </Typography>
                    <TextField value={otp} onChange={e=>setOtp(e.target.value)} fullWidth size="small"
                        placeholder="Enter OTP" InputProps={{ sx:{ borderRadius:"12px" } }} />
                </DialogContent>
                <DialogActions sx={{ px:3, pb:2, gap:1 }}>
                    <Button onClick={()=>{ setOtp(""); setOtpOpen(false); }} sx={{ borderRadius:"10px",textTransform:"none" }}>Cancel</Button>
                    <Button onClick={handleOtpSubmit} variant="contained" disableElevation sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk } }}>Verify</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top",horizontal:"center" }}>
                <Alert severity={snack.sev} variant="filled" onClose={()=>setSnack(s=>({...s,open:false}))} sx={{ borderRadius:"12px" }}>{snack.msg}</Alert>
            </Snackbar>
        </>
    );
}

// ── Main Settings Page ────────────────────────────────────────────────────
export default function Settings() {
    const navigate = useNavigate();

    return (
        <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

            {/* Hero header */}
            <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
                boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
                <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
                    <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
                        <SettingsRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Admin Settings</Typography>
                        <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>Configure system preferences, integrations, and platform settings</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Settings grid */}
            <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr", sm:"1fr 1fr", md:"1fr 1fr 1fr" }, gap:2 }}>

                {/* Account Settings */}
                <NavCard title="Account Settings" subtitle="Update your admin profile, contact details, and profile photo."
                    icon={<PersonRoundedIcon />} iconBg={T.tealLt} iconColor={T.teal} accent={T.teal}
                    onClick={()=>navigate("/app/profile")} />

                {/* Fee Structure & Taxation */}
                <NavCard title="Fee Structure & Taxation" subtitle="Platform fee percentages, GST rates, demand pricing, owner margins, and pricing engine configuration."
                    icon={<RequestQuoteRoundedIcon />} iconBg={T.indigoLt} iconColor={T.indigo} accent={T.indigo}
                    onClick={()=>navigate("/app/billing-settings")} />

                {/* Package Management */}
                <NavCard title="Package Management System" subtitle="Centralized package size categories with base pricing, handling fees, and per-kg rates."
                    icon={<InventoryRoundedIcon />} iconBg={T.orangeLt} iconColor={T.orange} accent={T.orange}
                    onClick={()=>navigate("/app/package-management")} />

                {/* Google Map Key */}
                <GoogleMapKeyCard />
            </Box>
        </Box>
    );
}
